/**
 * Cupom não fiscal — layout 80mm compartilhado pelo sistema e pelo caixa.
 * Dados da empresa vêm de Configurações > Geral (banco) via /api/config/cliente.php.
 */

export interface EmpresaConfig {
  nome: string;
  cnpj: string;
  endereco: string;
  telefone: string;
  ie?: string;
  logo_url?: string;
}

export interface ItemCupom {
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
  observacao?: string;
}

export interface VendaCupom {
  venda_id: number;
  data_hora: string;
  vendedor: string;
  itens: ItemCupom[];
  total_pecas: number;
  subtotal_geral: number;
  total_geral: number;
  desconto?: number;
  pagamento: { pix: number; dinheiro: number; credito: number; debito: number };
  /** Texto exibido no cupom não fiscal, ex.: "CPF: 000.000.000-00" ou "Cliente não identificado". */
  cliente_identificacao?: string;
}

let printingFiscalDocument = false;

export interface DanfeNfceSimplificado {
  ref?: string; 

  chave?: string;
  numero?: string;
  serie?: string;
  protocolo?: string;
  data_emissao?: string;
  qrcode_url?: string;
  qrcode_data_url?: string;
  url_consulta?: string;
  emitente?: {
    nome?: string;
    fantasia?: string;
    cnpj?: string;
    ie?: string;
    endereco?: string;
    logo_url?: string;
  };
  destinatario?: {
    cpf?: string;
    cnpj?: string;
    nome?: string;
  };
  itens?: Array<{
    descricao?: string;
    quantidade?: number;
    valor_unitario?: number;
    total?: number;
  }>;
  pagamentos?: Array<{
    descricao?: string;
    valor?: number;
  }>;
  subtotal?: number;
  desconto?: number;
  total?: number;
  valor_total_tributos?: number;
}

/** Fallback seguro quando a config do cliente ainda não carregou. */
export const EMPRESA_CUPOM_FALLBACK: EmpresaConfig = {
  nome: "GRANDSYSTEM",
  cnpj: "",
  endereco: "",
  telefone: "",
  logo_url: "/logo-grand-system.svg",
};

/** @deprecated Use empresa da config do cliente. Mantido para compatibilidade de import. */
export const EMPRESA_CUPOM = EMPRESA_CUPOM_FALLBACK;

interface CupomLayoutItem {
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  total: number;
  observacao?: string;
}

interface CupomLayoutEmitente {
  nome: string;
  cnpj: string;
  ie: string;
  endereco: string;
  telefone?: string;
  logo_url?: string;
}

interface CupomLayoutFiscal {
  chave: string;
  chaveFormatada: string;
  numero: string;
  serie: string;
  protocolo: string;
  dataEmissao: string;
  qrDataUrl: string;
  consultaUrl: string;
  consumidor: string;
  valorTotalTributos?: number;
}

interface CupomLayoutData {
  pageTitle: string;
  documentTitle: string;
  emitente: CupomLayoutEmitente;
  itens: CupomLayoutItem[];
  subtotal: number;
  desconto: number;
  total: number;
  formaPagamento: string;
  valorPago: number;
  consumidor?: string;
  fiscal?: CupomLayoutFiscal;
}

const CUPOM_LAYOUT_CSS = `
    @media print {
      @page { size: 80mm auto; margin: 0; }
      body { margin: 0; padding: 0; }
      .danfe-nfce {
        width: 76mm;
        max-width: 76mm;
        margin: 0 auto;
        padding: 15px 2mm 3mm 2mm;
        box-sizing: border-box;
        font-family: "Courier New", monospace;
        font-size: 9px;
        line-height: 1.2;
      }
    }

    html, body { width: 80mm; margin: 0; padding: 0; background: #fff; color: #000; }
    body { font-family: "Courier New", monospace; font-size: 9px; line-height: 1.2; font-weight: 700; color: #000 !important; }
    .wrap { width: 80mm; display: flex; justify-content: center; background: #fff; }
    .danfe-nfce { width: 76mm; max-width: 76mm; margin: 0 auto; padding: 15px 2mm 3mm 2mm; box-sizing: border-box; color: #000 !important; }
    .center { text-align: center; }
    .logo-empresa { display: block; margin: 10px auto 6px auto; width: 70px; height: auto; object-fit: contain; }
    .sep { border-top: 1px dashed #000; margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; font-size: inherit; table-layout: fixed; }
    th, td { padding: 2px 0; vertical-align: top; }
    th { border-bottom: 1px solid #000; text-align: left; }
    .right { text-align: right; }
    .center-text { text-align: center; }
    .bold { font-weight: 700; }
    .totals-table .total { font-weight: 700; font-size: 12px; }
    .qr-row { display: flex; gap: 10px; align-items: flex-start; margin-top: 10px; }
    .qr-row img { width: 92px; height: 92px; object-fit: contain; }
    .item-desc { max-width: 120px; white-space: nowrap; overflow: hidden; text-overflow: clip; }
    .small { font-size: 9px; word-break: break-word; }
    .chave-acesso {
      margin-top: 4px;
      font-size: 7.5px;
      line-height: 1.15;
      letter-spacing: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: clip;
      word-break: normal;
    }
    .row-between { display: flex; justify-content: space-between; gap: 10px; }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color: #000 !important;
      font-weight: 700;
      background: transparent !important;
    }
`;

function formatMoney(v: number): string {
  return `R$ ${v.toFixed(2).replace(".", ",")}`;
}

function sanitize(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function onlyDigits(s: string): string {
  return (s || "").replace(/\D/g, "");
}

function formatCnpj(v?: string): string {
  const d = onlyDigits(v || "");
  if (d.length !== 14) return v || "";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function formatCpf(v?: string): string {
  const d = onlyDigits(v || "");
  if (d.length !== 11) return v || "";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const sec = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${h}:${min}:${sec}`;
}

function truncate(text: string, max: number): string {
  const t = (text ?? "").trim();
  return t.length > max ? t.slice(0, max) : t;
}

function formatQtdCupom(q: number): string {
  return q.toFixed(3).replace(/\.?0+$/, "");
}

function renderItensTableRows(itens: CupomLayoutItem[]): string {
  if (!itens.length) return `<tr><td colspan="5">Sem itens disponíveis</td></tr>`;
  return itens
    .map((item) => {
      const qtd = Number(item.quantidade ?? 0);
      const unit = Number(item.valor_unitario ?? 0);
      const lineTotal = Number(item.total ?? qtd * unit);
      const obs = (item.observacao ?? "").trim();
      const obsRow = obs
        ? `<tr><td colspan="5" class="small" style="padding-top:0;padding-bottom:4px;">Obs.: ${sanitize(obs)}</td></tr>`
        : "";
      return `<tr>
  <td class="item-desc">${sanitize(truncate((item.descricao ?? "ITEM").trim() || "ITEM", 20))}</td>
  <td class="right">${formatQtdCupom(qtd)}</td>
  <td class="center-text">UN</td>
  <td class="right">${formatMoney(unit).replace("R$ ", "")}</td>
  <td class="right">${formatMoney(lineTotal).replace("R$ ", "")}</td>
</tr>${obsRow}`;
    })
    .join("");
}

function renderFiscalFooter(fiscal: CupomLayoutFiscal): string {
  const tributosLine =
    typeof fiscal.valorTotalTributos === "number" && Number.isFinite(fiscal.valorTotalTributos)
      ? `
        Tributos Totais Aproximados Incidentes (Lei Federal 12.741/2012): ${formatMoney(fiscal.valorTotalTributos)}<br/>
        Fonte: IBPT<br/><br/>`
      : "";
  return `
      <div class="sep"></div>
      <div class="small">
        <div class="center">Consulte pela chave de acesso em:</div>
        <div class="center">${sanitize(fiscal.consultaUrl)}</div>
        <div class="center chave-acesso">${sanitize(fiscal.chaveFormatada || fiscal.chave || "-")}</div>
      </div>
      <div class="qr-row">
        <img src="${fiscal.qrDataUrl}" alt="QR Code NFC-e" />
        <div class="small">
          Número: ${sanitize(fiscal.numero)}<br/>
          Série: ${sanitize(fiscal.serie)}<br/>
          Emissão: ${sanitize(fiscal.dataEmissao)}<br/>
          ${fiscal.consumidor}
          <br/><br/>
          <div class="bold">PROTOCOLO DE AUTORIZAÇÃO</div>
          ${sanitize(fiscal.protocolo)}
        </div>
      </div>
      <div class="sep"></div>
      <div class="center small">
        ${tributosLine}
        GrandSystem
      </div>`;
}

function renderNaoFiscalFooter(consumidor: string): string {
  return `
      <div class="sep"></div>
      <div class="center small">${sanitize(consumidor)}</div>
      <div class="sep"></div>
      <div class="center small">GrandSystem</div>`;
}

function renderCupomDocumentoHtml(data: CupomLayoutData): string {
  const emitente = data.emitente;
  const logoUrl =
    (emitente.logo_url && emitente.logo_url.trim()) ||
    EMPRESA_CUPOM_FALLBACK.logo_url ||
    "/logo-grand-system.svg";
  const emitenteNome = (emitente.nome || EMPRESA_CUPOM_FALLBACK.nome).toUpperCase();
  const emitenteCnpjRaw = (formatCnpj(emitente.cnpj) || emitente.cnpj || "").trim();
  const emitenteIeRaw = (emitente.ie ?? "").trim();
  const emitenteEnderecoRaw = (emitente.endereco ?? "").trim();
  const cnpjLine = emitenteCnpjRaw !== "" ? `<div>CNPJ: ${sanitize(emitenteCnpjRaw)}</div>` : "";
  const ieLine = emitenteIeRaw !== "" ? `<div>IE: ${sanitize(emitenteIeRaw)}</div>` : "";
  const enderecoLine =
    emitenteEnderecoRaw !== "" ? `<div>${sanitize(emitenteEnderecoRaw)}</div>` : "";
  const telefoneLine =
    emitente.telefone && emitente.telefone.trim() !== ""
      ? `<div>${sanitize(emitente.telefone.trim())}</div>`
      : "";
  const totalItens = data.itens.reduce((s, i) => s + Number(i.quantidade ?? 0), 0);
  const footer = data.fiscal
    ? renderFiscalFooter(data.fiscal)
    : renderNaoFiscalFooter(data.consumidor ?? "Consumidor: Não identificado");

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>${sanitize(data.pageTitle)}</title>
  <style>${CUPOM_LAYOUT_CSS}</style>
</head>
<body>
  <div class="wrap">
    <div class="danfe-nfce">
      <img class="logo-empresa" src="${sanitize(logoUrl)}" alt="${sanitize(emitenteNome)}" />
      <div class="center">
        <div class="bold">${sanitize(emitenteNome)}</div>
        ${cnpjLine}
        ${ieLine}
        ${enderecoLine}
        ${telefoneLine}
      </div>
      <div class="sep"></div>
      <div class="center bold">${sanitize(data.documentTitle)}</div>
      <div class="sep"></div>
      <table>
        <colgroup>
          <col style="width:auto;" />
          <col style="width:10mm;" />
          <col style="width:7mm;" />
          <col style="width:14mm;" />
          <col style="width:14mm;" />
        </colgroup>
        <thead>
          <tr>
            <th>DESCRIÇÃO</th>
            <th class="right">QTD</th>
            <th class="center-text">UN</th>
            <th class="right">UNIT</th>
            <th class="right">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          ${renderItensTableRows(data.itens)}
        </tbody>
      </table>
      <div class="sep"></div>
      <table class="totals-table">
        <tr><td>Qtd. total de itens</td><td class="right">${formatQtdCupom(totalItens)}</td></tr>
        <tr><td>Subtotal</td><td class="right">${formatMoney(data.subtotal)}</td></tr>
        <tr><td>Desconto</td><td class="right">${formatMoney(data.desconto)}</td></tr>
        <tr class="total"><td>TOTAL</td><td class="right">${formatMoney(data.total)}</td></tr>
      </table>
      <div class="sep"></div>
      <div class="small">
        <div class="row-between"><span>Forma de Pagamento</span><span>${sanitize(data.formaPagamento)}</span></div>
        <div class="row-between"><span>Valor Pago</span><span>${formatMoney(data.valorPago)}</span></div>
      </div>
      ${footer}
    </div>
  </div>
</body>
</html>`;
}

function formaPagamentoFromLista(
  pagamentos: Array<{ descricao?: string; valor?: number }>,
): string {
  if (pagamentos.length === 1) return pagamentos[0]?.descricao ?? "Pagamento";
  if (pagamentos.length > 1) return "Misto";
  return "Pagamento";
}

function pagamentosFromVendaCupom(venda: VendaCupom): Array<{ descricao: string; valor: number }> {
  const out: Array<{ descricao: string; valor: number }> = [];
  if (venda.pagamento.pix > 0) out.push({ descricao: "PIX", valor: venda.pagamento.pix });
  if (venda.pagamento.dinheiro > 0) out.push({ descricao: "Dinheiro", valor: venda.pagamento.dinheiro });
  if (venda.pagamento.credito > 0) out.push({ descricao: "Crédito", valor: venda.pagamento.credito });
  if (venda.pagamento.debito > 0) out.push({ descricao: "Débito", valor: venda.pagamento.debito });
  return out;
}

function formatConsumidorNaoFiscal(clienteIdentificacao?: string): string {
  const raw = (clienteIdentificacao ?? "").trim();
  if (!raw || /^cliente\s+n[aã]o\s+identificado$/i.test(raw)) {
    return "Consumidor: Não identificado";
  }
  if (/^(CPF|CNPJ):/i.test(raw)) {
    return `Consumidor: ${raw}`;
  }
  return `Consumidor: ${raw}`;
}

/**
 * Monta o HTML do cupom não fiscal reutilizando o layout da NFC-e.
 */
export function gerarCupomHtml(venda: VendaCupom, empresa: EmpresaConfig = EMPRESA_CUPOM_FALLBACK): string {
  const itens: CupomLayoutItem[] = venda.itens.map((item) => ({
    descricao: item.nome_produto || "ITEM",
    quantidade: item.quantidade,
    valor_unitario: item.preco_unitario,
    total: item.subtotal,
    observacao: item.observacao,
  }));
  const pagamentos = pagamentosFromVendaCupom(venda);
  const pagamentosTotal = pagamentos.reduce((s, p) => s + p.valor, 0);
  const desconto = venda.desconto ?? 0;

  return renderCupomDocumentoHtml({
    pageTitle: `Cupom ${venda.venda_id}`,
    documentTitle: "DOCUMENTO NÃO FISCAL",
    emitente: {
      nome: empresa.nome || EMPRESA_CUPOM_FALLBACK.nome,
      cnpj: empresa.cnpj || EMPRESA_CUPOM_FALLBACK.cnpj,
      ie: empresa.ie || "",
      endereco: empresa.endereco || EMPRESA_CUPOM_FALLBACK.endereco,
      telefone: empresa.telefone,
      logo_url: empresa.logo_url,
    },
    itens,
    subtotal: venda.subtotal_geral,
    desconto,
    total: venda.total_geral,
    formaPagamento: formaPagamentoFromLista(pagamentos),
    valorPago: pagamentosTotal || venda.total_geral,
    consumidor: formatConsumidorNaoFiscal(venda.cliente_identificacao),
  });
}

/**
 * Abre janela ou iframe, escreve o HTML do cupom e chama print().
 */
/**
 * Abre URL do DANFE/PDF (retorno Focus ou similar) e dispara impressão.
 * PDFs em outro domínio podem não permitir print() automático; o usuário pode usar Ctrl+P na aba.
 */
export function imprimirDocumentoFiscalPorUrl(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (printingFiscalDocument) {
      resolve();
      return;
    }
    printingFiscalDocument = true;
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      let done = false;
      const finish = (err?: Error) => {
        if (done) return;
        done = true;
        try {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        } catch {
          // ignore
        }
        printingFiscalDocument = false;
        if (err) {
          reject(err);
          return;
        }
        resolve();
      };

      const tryPrintFromIframe = () => {
        try {
          const win = iframe.contentWindow;
          if (!win) throw new Error("iframe sem contentWindow");
          win.focus();
          win.print();
        } catch {
          finish(new Error("Não foi possível abrir a impressão automaticamente. Abra o DANFE manualmente."));
          return;
        }
        window.setTimeout(() => finish(), 1200);
      };

      iframe.onload = () => {
        window.setTimeout(tryPrintFromIframe, 250);
      };

      iframe.src = url;
      // Fallback para casos em que onload não dispara (especialmente PDF remoto).
      window.setTimeout(tryPrintFromIframe, 2200);
      window.setTimeout(() => finish(), 4000);
    } catch (e) {
      printingFiscalDocument = false;
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

async function gerarDanfeNfceSimplificadoHtml(dados: DanfeNfceSimplificado): Promise<string> {
  const emitente = dados.emitente ?? {};
  const destinatario = dados.destinatario ?? {};
  const itens = dados.itens ?? [];
  const pagamentos = dados.pagamentos ?? [];
  const subtotalCalculado = dados.subtotal ?? itens.reduce((s, i) => s + (i.total ?? 0), 0);
  const desconto = dados.desconto ?? 0;
  const total = dados.total ?? Math.max(0, subtotalCalculado - desconto);
  const qrDataUrl = (dados.qrcode_data_url && dados.qrcode_data_url.trim()) || "";
  if (!qrDataUrl) {
    throw new Error("QR Code da NFC-e ainda não disponível. Aguarde e tente imprimir novamente.");
  }

  const pagamentosTotal = pagamentos.reduce((s, p) => s + Number(p.valor ?? 0), 0);
  const chave = dados.chave?.trim() ?? "";
  const dataEmissao = formatDate(dados.data_emissao?.trim() ?? new Date().toISOString());
  const cnpjDest = onlyDigits(destinatario.cnpj ?? "");
  const cpfDest = onlyDigits(destinatario.cpf ?? "");
  const consumidor =
    cnpjDest.length === 14
      ? `CNPJ: ${sanitize(formatCnpj(cnpjDest) || cnpjDest)}`
      : cpfDest.length === 11
        ? `CPF: ${sanitize(formatCpf(cpfDest) || cpfDest)}`
        : "Consumidor não identificado";
  const chaveFormatada = chave.replace(/\s+/g, "");
  const protocoloLine = [dados.protocolo ?? "-", dataEmissao].join(" - ");
  const consultaUrl =
    (dados.url_consulta && dados.url_consulta.trim()) ||
    "https://sat.sef.sc.gov.br/nfce/consulta";

  return renderCupomDocumentoHtml({
    pageTitle: `DANFE NFC-e ${dados.numero?.trim() || ""}`,
    documentTitle: "DOCUMENTO AUXILIAR DA NOTA FISCAL DE CONSUMIDOR",
    emitente: {
      nome: (emitente.nome ?? emitente.fantasia ?? EMPRESA_CUPOM_FALLBACK.nome).toUpperCase(),
      cnpj: formatCnpj(emitente.cnpj ?? "") || emitente.cnpj || "",
      ie: (emitente.ie && emitente.ie.trim() !== "" ? emitente.ie : "") || "",
      endereco: (emitente.endereco ?? "").trim(),
      logo_url:
        (emitente.logo_url && emitente.logo_url.trim()) ||
        EMPRESA_CUPOM_FALLBACK.logo_url ||
        "/logo-grand-system.svg",
    },
    itens: itens.map((item) => ({
      descricao: (item.descricao ?? "ITEM").trim() || "ITEM",
      quantidade: Number(item.quantidade ?? 0),
      valor_unitario: Number(item.valor_unitario ?? 0),
      total: Number(item.total ?? Number(item.quantidade ?? 0) * Number(item.valor_unitario ?? 0)),
    })),
    subtotal: subtotalCalculado,
    desconto,
    total,
    formaPagamento: formaPagamentoFromLista(pagamentos),
    valorPago: pagamentosTotal || total,
    fiscal: {
      chave,
      chaveFormatada,
      numero: dados.numero ?? "-",
      serie: dados.serie ?? "-",
      protocolo: protocoloLine,
      dataEmissao,
      qrDataUrl,
      consultaUrl,
      consumidor,
      valorTotalTributos: dados.valor_total_tributos,
    },
  });
}

export async function imprimirDanfeNfceSimplificado(dados: DanfeNfceSimplificado): Promise<void> {
  const html = await gerarDanfeNfceSimplificadoHtml(dados);
  await imprimirCupom(html);
}

export function imprimirCupom(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    let win: Window | null = null;
    try {
      win = window.open("", "_blank", "width=400,height=600,menubar=no,toolbar=no,location=no");
      if (!win) {
        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.left = "-9999px";
        iframe.style.top = "0";
        iframe.style.width = "80mm";
        iframe.style.border = "none";
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow?.document;
        if (!doc) {
          document.body.removeChild(iframe);
          reject(new Error("Não foi possível abrir área de impressão"));
          return;
        }
        doc.open();
        doc.write(html);
        doc.close();
        setTimeout(() => {
          try {
            iframe.contentWindow?.print();
          } catch (e) {
            reject(e);
          }
          setTimeout(() => {
            document.body.removeChild(iframe);
            resolve();
          }, 800);
        }, 150);
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => {
        try {
          win!.print();
        } catch (e) {
          win!.close();
          reject(e);
          return;
        }
        setTimeout(() => {
          if (win && !win.closed) win.close();
          resolve();
        }, 800);
      }, 150);
    } catch (e) {
      if (win && !win.closed) win.close();
      reject(e);
    }
  });
}
